export type VideoPlatform = 'youtube' | 'vimeo'

export interface ParsedVideo {
  platform: VideoPlatform
  videoId: string
  originalUrl: string
}

// Pattern format: [regex, platform]
// YouTube formats:
// - https://www.youtube.com/watch?v=VIDEO_ID
// - https://youtu.be/VIDEO_ID
// - https://www.youtube.com/embed/VIDEO_ID
// - https://m.youtube.com/watch?v=VIDEO_ID
//
// Vimeo formats:
// - https://vimeo.com/VIDEO_ID
// - https://player.vimeo.com/video/VIDEO_ID
const patterns: Array<{
  regex: RegExp
  platform: VideoPlatform
}> = [
  // YouTube patterns
  {
    regex: /https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    platform: 'youtube',
  },
  {
    regex: /https?:\/\/youtu\.be\/([a-zA-Z0-9_-]{11})/,
    platform: 'youtube',
  },
  {
    regex: /https?:\/\/(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    platform: 'youtube',
  },
  {
    regex: /https?:\/\/m\.youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    platform: 'youtube',
  },
  // Vimeo patterns
  {
    regex: /https?:\/\/vimeo\.com\/(\d+)/,
    platform: 'vimeo',
  },
  {
    regex: /https?:\/\/player\.vimeo\.com\/video\/(\d+)/,
    platform: 'vimeo',
  },
]

/**
 * Extracts video links (YouTube and Vimeo) from a markdown/text description.
 * Maintains the order videos appear in the description.
 * Deduplicates by video ID - different URL formats for the same video are deduplicated.
 * @param description - text description to parse
 * @returns Array of parsed videos in order of appearance, deduplicated by video ID
 */
export function extractVideosFromDescription(
  description?: string
): ParsedVideo[] {
  if (!description || typeof description !== 'string') {
    return []
  }

  const videos: ParsedVideo[] = []
  const seenIds = new Set<string>() // Track "{platform}-{videoId}" to deduplicate

  // Extract all URLs from description (preserves order)
  const urlArray = description.match(/https?:\/\/[^\s)]+/g) || []

  // Loop through each URL and check if it matches YouTube or Vimeo patterns
  for (const url of urlArray) {
    // Check each pattern to find a match
    for (const { regex, platform } of patterns) {
      const match = url.match(regex)

      if (match?.[1]) {
        const videoId = match[1]
        const uniqueKey = `${platform}-${videoId}`

        if (!seenIds.has(uniqueKey)) {
          // Store ID in seenIds Set to check for duplicates
          seenIds.add(uniqueKey)

          videos.push({
            platform,
            videoId,
            originalUrl: url,
          })
        }

        break // Found a match for this URL, move to next URL
      }
    }
  }

  return videos
}
